import { useEffect, useState } from 'react'

import { getProductMedia } from '@/lib/api/client/inventory'
import type { ProductMedia } from '@/lib/api/schemas/inventory.schema'
import { WarehouseThumbnail } from '@/components/warehouse/WarehouseThumbnail'
import { unavailableReasonText } from '@/lib/warehouse/labels'

type PhotoState =
  | { kind: 'loading' }
  | { kind: 'available'; mediaUrl: string; label: string }
  | { kind: 'absent'; reason: string | null; message: string }

/**
 * One candidate's photograph, where there is one.
 *
 * 42 of 4,607 pieces carry a downloaded photograph, so this renders nothing at
 * all for roughly ninety-nine candidates in a hundred. That is why it is the
 * last thing in a candidate and never the thing the card is built around: an
 * empty square repeated eight times is a screen that looks broken while telling
 * the operator nothing.
 *
 * The absence sentence is the server's own, from the product media route, so one
 * missing photograph reads the same here as it does in the Warehouse.
 */
export function CandidatePhoto({ productId, label }: { productId: string; label: string }) {
  const [state, setState] = useState<PhotoState>({ kind: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ kind: 'loading' })
    getProductMedia(productId, controller.signal)
      .then((media: ProductMedia) => {
        if (controller.signal.aborted) return
        const primary = media.items.find((item) => item.isPrimary) ?? media.items[0]
        if (primary !== undefined && primary.mediaUrl !== null) {
          setState({ kind: 'available', mediaUrl: primary.mediaUrl, label })
          return
        }
        const reason = primary?.unavailableReason ?? media.unavailableReason
        const message = primary?.unavailableMessage
          ?? media.unavailableMessage
          ?? (reason === null || reason === undefined
            ? 'Фотография не опубликована.'
            : unavailableReasonText(reason))
        setState({ kind: 'absent', reason: reason ?? null, message })
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setState({ kind: 'absent', reason: null, message: 'Фотографию сейчас не прочитать.' })
      })
    return () => controller.abort()
  }, [label, productId])

  if (state.kind === 'loading') {
    return (
      <span className="text-[11px] text-ink-muted" data-photo-state="loading">
        Фотография: читаем…
      </span>
    )
  }
  if (state.kind === 'available') {
    return (
      <span className="inline-flex items-center gap-1.5" data-photo-state="available">
        <WarehouseThumbnail mediaUrl={state.mediaUrl} label={state.label} size="large" />
        <span className="text-[11px] text-ink-muted">Фотография есть</span>
      </span>
    )
  }
  return (
    <span
      className="text-[11px] text-ink-muted"
      data-photo-state="absent"
      data-reason={state.reason ?? undefined}
    >
      {state.message}
    </span>
  )
}
